import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";

import Dashboard from "./components/Dashboard";
import NewAssessmentForm from "./components/NewAssessmentForm";
import AssessmentDetails from "./components/AssessmentDetails";
import VulnerabilityForm from "./components/VulnerabilityForm";
import Login from "./components/Login";
// Removed SessionTimeout import
import { api, apiEndpoints } from "./api/api";
import "./App.css";

const Header = ({ user, onLogout }) => <header className="app-header"></header>;

function AppContent() {
  const [user, setUser] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessmentData, setSelectedAssessmentData] = useState({
    assessment: null,
    version: null,
  });
  const [vulnerabilityToEdit, setVulnerabilityToEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const { assessment: selectedAssessment, version: selectedVersion } =
    selectedAssessmentData;

  // Removed all state and refs related to session timeout
  // (SESSION_TIMEOUT_SECONDS, remainingTime, timerRef, resetTimer)

  const handleLogout = useCallback(async () => {
    try {
      await api.post(apiEndpoints.logout);
    } catch (error) {
      console.error("Logout failed on server:", error);
    } finally {
      setUser(null);
      setAssessments([]);
      setSelectedAssessmentData({ assessment: null, version: null });
      setVulnerabilityToEdit(null);
      // Removed clearInterval from here
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data } = await api.get(apiEndpoints.me);
        setUser(data);
      } catch (error) {
        console.log("No active session.");
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, []);

  // Removed the useEffect that handled the session timer countdown.

  const fetchAssessments = useCallback(async () => {
    if (!user) {
      setAssessments([]);
      return;
    }
    console.log("Fetching assessments for user:", user);
    const url = user.roles?.includes("Client")
      ? apiEndpoints.assessmentsByClientId(user.id)
      : apiEndpoints.assessments;
    try {
      // Directly calling the api, removed the timeout reset wrapper
      const { data } = await api.get(url);
      if (Array.isArray(data)) {
        setAssessments(data);
      } else {
        console.warn(
          "API did not return an array for assessments. Response:",
          data
        );
        setAssessments([]);
      }
    } catch (err) {
      console.error("Error fetching assessments:", err);
      setAssessments([]);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      // Only fetch assessments if a user is logged in
      fetchAssessments();
    }
  }, [user, fetchAssessments]);

  const addAssessment = async (newAssessment) => {
    try {
      // Directly calling the api
      await api.post(apiEndpoints.assessments, newAssessment);
      await fetchAssessments();
      return true;
    } catch (error) {
      alert(
        `Failed to save assessment: ${
          error.response?.data?.message || error.message
        }`
      );
      return false;
    }
  };

  const updateAssessment = async (updatedAssessment) => {
    const url = apiEndpoints.assessment(updatedAssessment.assessmentId);
    try {
      // Directly calling the api
      await api.put(url, updatedAssessment);
      await fetchAssessments();
      return true;
    } catch (error) {
      alert(`Failed to update assessment: ${error.message}`);
      return false;
    }
  };

  const handleLoginSuccess = (loggedInUser) => {
    setUser(loggedInUser);
    // Removed resetTimer() call
    navigate("/dashboard");
  };

  const handleSaveVulnerability = async () => {
    await fetchAssessments();
    if (selectedAssessment) {
      const updatedAssessment = assessments.find(
        (a) => a.assessmentId === selectedAssessment.assessmentId
      );
      if (updatedAssessment) {
        const updatedVersion = updatedAssessment.versions.find(
          (v) => v.assessmentVersionId === selectedVersion?.assessmentVersionId
        );
        setSelectedAssessmentData({
          assessment: updatedAssessment,
          version: updatedVersion || null,
        });
      }
    }
    setVulnerabilityToEdit(null);
  };

  const handleViewDetails = (assessment) => {
    setSelectedAssessmentData({
      assessment: assessment,
      version: assessment.versions?.[0] || null,
    });
    navigate(`/assessment/${assessment.assessmentId}`);
  };

  const handleEditAssessment = (assessmentToEdit) => {
    // We can use the navigate function you already have
    navigate(`/assessment/${assessmentToEdit.assessmentId}/edit`);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="app-container">
      {user && (
        <>
          {/* Removed SessionTimeout component */}
          <Header user={user} onLogout={handleLogout} />
        </>
      )}
      <Routes>
        {!user ? (
          <>
            <Route
              path="/login"
              element={<Login onLoginSuccess={handleLoginSuccess} />}
            />
            <Route path="*" element={<Navigate to="/login" />} />
          </>
        ) : (
          <>
            <Route
              path="/dashboard"
              element={
                <Dashboard
                  assessments={assessments}
                  onViewDetails={handleViewDetails}
                  userRoles={user.roles || []}
                  onLogout={handleLogout}
                  currentUser={user}
                  // Removed remainingTime prop
                />
              }
            />
            <Route
              path="/assessment/new"
              element={<NewAssessmentForm onSave={addAssessment} currentUser={user} />}
            />
            <Route
              path="/assessment/:id"
              element={
                <AssessmentDetails
                  assessment={selectedAssessment}
                  onSelectVersion={(version) =>
                    setSelectedAssessmentData((prev) => ({ ...prev, version }))
                  }
                  onEditVulnerability={(v) => setVulnerabilityToEdit(v)}
                  userRoles={user.roles || []}
                  onEditAssessment={handleEditAssessment}
                  currentUser={user}
                />
              }
            />
            <Route
              path="/assessment/:id/edit"
              element={
                <NewAssessmentForm
                  assessmentToEdit={selectedAssessment}
                  onSave={updateAssessment}
                  currentUser={user}
                />
              }
            />
            <Route
              path="/assessment/:id/vulnerability/new"
              element={
                <VulnerabilityForm
                  selectedAssessment={selectedAssessment}
                  selectedVersion={selectedVersion}
                  selectedVulnerability={null}
                  onSave={handleSaveVulnerability}
                  userRoles={user.roles || []}
                  assessmentStatus={selectedAssessment?.status?.name}
                  currentUser={user}
                />
              }
            />
            <Route
              path="/assessment/:id/vulnerability/:vulnId/edit"
              element={
                <VulnerabilityForm
                  selectedAssessment={selectedAssessment}
                  selectedVersion={selectedVersion}
                  selectedVulnerability={vulnerabilityToEdit}
                  onSave={handleSaveVulnerability}
                  userRoles={user.roles || []}
                  assessmentStatus={selectedAssessment?.status?.name}
                  currentUser={user}
                />
              }
            />
            <Route path="*" element={<Navigate to="/dashboard" currentUser={user} />} />
          </>
        )}
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
